import '../ProductCard.scss';
import './MobileProductCard.scss';

import classnames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import loadable from '@loadable/component';

import { listerClickThrough } from '../../../actions/lister-actions';
import { useIsBelowXSmallOnly } from '../../../hooks/mediaQueries';
import { useEventListener } from '../../../hooks/useEventListener';
import { useIsProductInHighDemand } from '../../../hooks/useIsProductInHighDemand';
import TRANSLATIONS from '../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../utilities/lister-utilities';
import { isProductInTradingRange } from '../../../utilities/product-utilities';
import { Context } from '../../Views/store/store';
import AOWeRecommendSmile from '../AOWeRecommendSmile';
import Bullets from '../Bullets';
import EnergyInformation from '../EnergyInformation/EnergyInformation';
import NewEnergyInformation from '../EnergyInformation/NewEnergyInformation';
import FinanceProposition from '../FinanceProposition';
import Heading from '../Heading';
import HighDemandFlag from '../HighDemand/HighDemandFlag';
import HighDemandMessaging from '../HighDemand/HighDemandMessaging';
import PricePod from '../PricePod/PricePod@default';
import ProductImage from '../ProductImage/ProductImage';
import PromoFlag from '../PromoFlag';
import ReviewRating from '../ReviewRating/ReviewRating';
import StockStatus from '../StockStatus';
import TradingRangeFlag from '../TradingRangeFlag/TradingRangeFlag';
import MobileDeliveryMessageBlock from './MobileDeliveryMessage/MobileDeliveryMessageBlock@default';
import MobileProductCardSkeleton from './Skeletons/MobileProductCardSkeleton';

const BackInStockForm = loadable(() =>
	import(/* webpackPrefetch: true */ '../BackInStockForm/BackInStockForm.js')
);

const AddToBasketButton = loadable(() =>
	import('../AddToBasket/AddToBasketButton@mobile')
);
const MoreInfo = loadable(() => import('../MoreInfo'));

const RecommendationCarousel = loadable(() =>
	import('../RecommendationCarousel/RecommendationCarousel')
);

export const MobileProductCard = ({ index, product, observer, page }) => {
	const card = useRef(null);

	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	const {
		ListerAlternativeProductsEnabled,
		ListerProductCardEnergyLabelEnabled,
		ListerAddToBasketMobileButtonEnabled,
	} = useSelector((state) => state.experimentSwitches);

	const dispatch = useDispatch();

	const isSkuHighDemand = useIsProductInHighDemand(product);

	const isBelowXSmallOnly = useIsBelowXSmallOnly();

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	useEffect(() => {
		const cardRef = card.current;

		if (observer && cardRef) {
			observer.observe(cardRef);
		}

		return () => {
			if (observer && cardRef) {
				observer.unobserve(cardRef);
			}
		};
	}, [card.current, observer]);

	if (!product) {
		return null;
	}

	const tradingRangeInfo = isProductInTradingRange(product.Code);

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	if (!loaded) {
		return <MobileProductCardSkeleton ColourId={product.ColourId} />;
	}

	return (
		<li
			ref={card}
			data-page={page}
			id={product.Code}
			className="xs:p-2 w-full mobile-outer"
			style={{ WebkitTransform: 'translateZ(0)' }}
			itemProp="itemListElement"
			itemScope
			itemType="http://schema.org/ListItem"
			data-testid={
				product.IsFeaturedProduct
					? 'product-card--featured'
					: 'product-card--standard'
			}>
			<meta itemProp="position" content={index} />

			<div
				className={classnames('product-card', 'mobile-border', {
					'product-card--out-of-stock': product.State === 'OutOfStock',
					'flex flex-col h-full bg-darkgreen border-t-0 pb-2 px-2 relative overflow-hidden xs:rounded-lg':
						product.IsFeaturedProduct,
				})}
				data-testid={
					product.State === 'OutOfStock'
						? 'product-state--out-of-stock'
						: 'product-state--available'
				}>
				{product.IsFeaturedProduct && (
					<React.Fragment>
						<div className="text-title-sm text-white py-2 px-1">
							{TRANSLATIONS.productList.productCard.featuredProductFlag}
						</div>

						<AOWeRecommendSmile
							className="absolute"
							style={{ top: '-170px', left: '-110px' }}
						/>
					</React.Fragment>
				)}

				<a
					href={product.FullProductUrl}
					onClick={(event) => {
						event.preventDefault();
						window.location.assign(product.FullProductUrl);

						dispatch(listerClickThrough(product.Code));
					}}
					className="product-card__link"
					itemProp="url"
					data-testid="product-card-link"
					data-tag-name="lister product card"
					data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
					data-tag-delivery-date={deliveryDate}
					data-tag-delivery-days={deliveryDaysDifference}>
					<span>
						{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
					</span>
				</a>

				<div
					className={classnames(
						'mobile-border',
						'flex flex-col bg-white p-4 xs:rounded-lg relative',
						{
							'trading-range--enabled pt-6 sm:pt-8': tradingRangeInfo,
							'border border-solid border-gray-30': !product.IsFeaturedProduct,
						}
					)}
					style={{ flex: '1' }}
					itemScope
					itemType="http://schema.org/Product"
					data-productcode={product.Code}
					data-testid="product-card-mobile-view">
					{tradingRangeInfo && <TradingRangeFlag {...tradingRangeInfo} small />}
					<div className="flex">
						<div className="product-card__mobile-image-col flex flex-col justify-between w-1/3 p-2 pl-0">
							<div>
								<ProductImage {...product} />
								<StockStatus
									classes="mt-4"
									stockStatus={product.State}
									productCode={product.Code}
									alignCenter
								/>

								{isSkuHighDemand && <HighDemandFlag />}

								{product.EnergyRating && (
									<EnergyInformation
										classes="mt-4 text-center z-10"
										energyRatingDetails={product.EnergyRatingDetails}
									/>
								)}
							</div>

							{!isSkuHighDemand && (
								<MobileDeliveryMessageBlock
									deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
									stockStatus={product.State}
								/>
							)}
						</div>

						<div className="w-2/3 p-2 pr-0">
							<Heading content={product.FullTitle} />
							<ReviewRating classes="mt-2" {...product} />

							<PricePod viewModel={product.PricePodViewModel} />

							{product.State !== 'OutOfStock' ? (
								<FinanceProposition {...product} />
							) : null}

							{ListerProductCardEnergyLabelEnabled && (
								<NewEnergyInformation
									energyRatingDetails={product.EnergyRatingDetails}
								/>
							)}

							<PromoFlag {...product} />
							<Bullets content={product.FeatureDescriptions} />
						</div>
					</div>

					<div className="z-10 mt-auto">
						{ListerAddToBasketMobileButtonEnabled &&
							!isSkuHighDemand &&
							product.State !== 'OutOfStock' && (
								<div className="flex z-10 py-3">
									<MoreInfo {...product} />

									<AddToBasketButton {...product} />
								</div>
							)}

						{isSkuHighDemand && <HighDemandMessaging classes="mb-2" />}

						{product.State === 'OutOfStock' && (
							<BackInStockForm
								productCode={product.Code}
								productUrl={product.ProductUrl}
								productColourId={product.ColourId}
								productState={product.State}
							/>
						)}

						{ListerAlternativeProductsEnabled &&
							isBelowXSmallOnly &&
							product.State === 'OutOfStock' && (
								<RecommendationCarousel
									NowPrice={product.PricePodViewModel.NowPrice}
								/>
							)}
					</div>
				</div>
			</div>
		</li>
	);
};

export default MobileProductCard;
